import format from 'date-fns/format'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import isWithinInterval from 'date-fns/isWithinInterval'
import startOfDay from 'date-fns/startOfDay'
import { colors, parseURLParams } from '@offerzen/pinata-shame'

import { parseDate } from '@/core/helpers/dateTimeHelper'
import CheckCircleIcon from '@offerzen/zenicon/CheckCircleIcon'
import ClockIcon from '@offerzen/zenicon/ClockIcon'
import CloseCircleIcon from '@offerzen/zenicon/CloseCircleIcon'

export const schedulingUrlParamsExist = urlParams => !!parseURLParams(urlParams).feedback_for || !!parseURLParams(urlParams).auth_success

/**
 * Extracts properties from a selected time slot.
 *
 * @param {Object} selectedTimeSlot - The selected time slot object.
 * @param {Date} selectedTimeSlot.start - The start date of the selected time slot.
 * @param {Date} selectedTimeSlot.end - The end date of the selected time slot.
 * @returns {Object} An object containing the extracted properties.
 * @property {string} selectedDate - The formatted date string (e.g., 'Monday, 27 July').
 * @property {string} selectedTime - The formatted time range string (e.g., '12:00 - 14:30').
 * @property {number} selectedDuration - The duration in minutes between start and end.
 */
export const selectedTimeSlotProperties = selectedTimeSlot => {
  let selectedDate = ''
  let selectedTime = ''
  let selectedDuration = ''

  if (selectedTimeSlot) {
    selectedDate = format(selectedTimeSlot.start, 'EEEE, d MMMM')
    selectedTime = `${format(selectedTimeSlot.start, 'HH:mm')} - ${format(selectedTimeSlot.end, 'HH:mm')}`
    selectedDuration = differenceInMinutes(selectedTimeSlot.end, selectedTimeSlot.start)
  }

  return {
    selectedDate,
    selectedTime,
    selectedDuration,
  }
}

export const isSelectionOutsideAvailability = (selectedTimeSlot, availableTimeSlots) => {
  let selectionOutsideAvailability = false

  if (selectedTimeSlot && Object.values(availableTimeSlots).length) {
    const selectionAvailabilityDay = availableTimeSlots[startOfDay(selectedTimeSlot.start)] || []

    const startInRange = selectionAvailabilityDay.find(range =>
      isWithinInterval(selectedTimeSlot.start, { start: parseDate(range.startDate), end: parseDate(range.endDate) }),
    )
    const endInRange = selectionAvailabilityDay.find(range =>
      isWithinInterval(selectedTimeSlot.end, { start: parseDate(range.startDate), end: parseDate(range.endDate) }),
    )

    if (startInRange && endInRange) {
      selectionOutsideAvailability = startInRange.id !== endInRange.id
    } else {
      selectionOutsideAvailability = !startInRange || !endInRange
    }
  }

  return selectionOutsideAvailability
}

// GraphQL interview event helpers
// enum of interview event status
const InterviewEventStatus = {
  Cancelled: 'cancelled',
  Confirmed: 'confirmed',
  Pending: 'pending',
  Declined: 'declined',
}

Object.freeze(InterviewEventStatus)

export const interviewEventStatusMap = {
  [InterviewEventStatus.Pending]: {
    id: 'pending',
    label: 'Pending',
    icon: ClockIcon,
    theme: 'light',
    colour: colors.jumbo,
  },
  [InterviewEventStatus.Confirmed]: {
    id: 'confirmed',
    label: 'Confirmed',
    icon: CheckCircleIcon,
    theme: 'primary',
    colour: colors.cornflowerBlue,
  },
  [InterviewEventStatus.Cancelled]: {
    id: 'cancelled',
    label: 'Cancelled',
    icon: CloseCircleIcon,
    theme: 'error',
    colour: colors.mandy,
  },
  [InterviewEventStatus.Declined]: {
    id: 'declined',
    label: 'Declined',
    icon: CloseCircleIcon,
    theme: 'error',
    colour: colors.mandy,
  },
}

// Keeping this around to cater for past events that have been created by Cronofy
const InterviewEventPoweredBy = {
  Product: 'PRODUCT',
  Cronofy: 'CRONOFY',
}

Object.freeze(InterviewEventPoweredBy)

// enum of interview event source
const InterviewEventSource = {
  Platform: 'PLATFORM',
  Ats: 'ATS',
}

Object.freeze(InterviewEventSource)

// corresponds to statuses filter on interviewEvents graphql
const InterviewEventStatusFilter = {
  Cancelled: 'CANCELLED',
  Confirmed: 'CONFIRMED',
  Pending: 'PENDING',
  Declined: 'DECLINED',
}

Object.freeze(InterviewEventStatusFilter)

// corresponds to InterviewEventPeriod in graphql
const InterviewEventPeriod = {
  Past: 'PAST',
  Pending: 'PENDING',
  Upcoming: 'UPCOMING',
}

Object.freeze(InterviewEventPeriod)

// enum of interview event category
const InterviewEventCategory = {
  InPerson: 'IN_PERSON',
  PhoneCall: 'PHONE_CALL',
  VideoCall: 'VIDEO_CALL',
  SkypeCall: 'SKYPE_CALL',
}

Object.freeze(InterviewEventCategory)

const interviewEventCategoryMap = {
  [InterviewEventCategory.InPerson]: {
    id: InterviewEventCategory.InPerson,
    label: 'In-person',
    details: ['location'],
  },
  [InterviewEventCategory.VideoCall]: {
    id: InterviewEventCategory.VideoCall,
    label: 'Video call',
    details: ['videoCallUrl'],
  },
  [InterviewEventCategory.SkypeCall]: {
    id: InterviewEventCategory.SkypeCall,
    label: 'Skype',
    details: ['skypeHandleMember', 'skypeHandleCandidate'],
  },
  [InterviewEventCategory.PhoneCall]: {
    id: InterviewEventCategory.PhoneCall,
    label: 'Phone',
    details: ['phoneNumberCandidate'],
  },
}

const durations = [
  {
    value: 15,
    label: '15 min',
  },
  {
    value: 30,
    label: '30 min',
  },
  {
    value: 45,
    label: '45 min',
  },
  {
    value: 60,
    label: '1 hour',
  },
  {
    value: 90,
    label: '1.5 hours',
  },
  {
    value: 120,
    label: '2 hours',
  },
  {
    value: 180,
    label: '3 hours',
  },
  {
    value: 240,
    label: '4 hours',
  },
  {
    value: 300,
    label: '5 hours',
  },
  {
    value: 360,
    label: '6 hours',
  },
  {
    value: 420,
    label: '7 hours',
  },
  {
    value: 480,
    label: '8 hours',
  },
]

const interviewTypeOptions = [
  interviewEventCategoryMap[InterviewEventCategory.InPerson],
  interviewEventCategoryMap[InterviewEventCategory.VideoCall],
  interviewEventCategoryMap[InterviewEventCategory.PhoneCall],
]

export {
  InterviewEventCategory,
  InterviewEventPoweredBy,
  InterviewEventStatus,
  InterviewEventStatusFilter,
  InterviewEventSource,
  InterviewEventPeriod,
  interviewEventCategoryMap,
  durations,
  interviewTypeOptions,
}
